import { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePadDialog from './DatePad';

const MIN_YEAR = 1900;

/** "YYYY-MM-DD" → 8 chữ số "DDMMYYYY" để hiện lại trên bàn số */
const toDigits = (ymd: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  return m ? `${m[3]}${m[2]}${m[1]}` : '';
};

/** Kiểm tra 8 chữ số "DDMMYYYY"; hợp lệ thì trả về "YYYY-MM-DD", không thì null */
const parseDob = (digits: string) => {
  if (digits.length !== 8) return null;
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4));
  const date = new Date(year, month - 1, day);
  // Date tự tràn ngày (31/02 → 03/03) nên so lại từng phần để bắt ngày không có thật
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  if (year < MIN_YEAR || date > new Date()) return null;
  return `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
};

const ageOf = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const now = new Date();
  return now.getFullYear() - y - (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d) ? 1 : 0);
};

interface Props {
  /** "YYYY-MM-DD" hoặc rỗng */
  value: string;
  onChange: (ymd: string) => void;
}

/**
 * Chọn ngày sinh cho kiosk: chạm ô để mở bàn số, khách gõ liền ngày tháng năm (tối đa 8 lần chạm).
 * Lịch hệ thống phải cuộn ngược hàng chục năm nên không dùng.
 */
export default function DobPicker({ value, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const digits = toDigits(value);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white flex items-center justify-between gap-2 hover:border-spa-dark transition-colors">
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>
          {value ? `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}` : t('checkin.dob.placeholder')}
        </span>
        <CalendarDays size={16} className="text-gray-400 shrink-0" />
      </button>

      {open && (
        <DatePadDialog
          title={t('checkin.dob.title')}
          hint={t('checkin.dob.hint')}
          mask={t('checkin.dob.mask')}
          initialDigits={digits}
          parse={parseDob}
          describe={(ymd) => t('checkin.dob.age', { age: ageOf(ymd) })}
          invalidText={t('checkin.dob.invalid')}
          onDone={(ymd) => { onChange(ymd); setOpen(false); }}
          onClear={value ? () => { onChange(''); setOpen(false); } : undefined}
          clearText={t('checkin.dob.clear')}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
